import React from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import { FormSpy } from "react-final-form";
import createDecorator from "final-form-calculate";

import { Button, Typography } from "@material-ui/core";

import FormControl, {
  TextFieldControl,
  DateFieldControl,
  CheckboxGroupWithSwitchFieldControl,
  UploadPreviewFieldControl,
  DropdownFieldControl,
  RadioButtonGroupControl,
} from "components/FormControl";
import SubmitButtonControl from "components/FormControl/SubmitButtonControl";
import LoadingButton from "components/LoadingButton";
import Row from "components/Form/Row";
import Loader from "components/Layout/Loader";
import Popup from "components/Popup";
import ConfirmationDialog from "components/ConfirmationDialog";
import PublishStatus from "components/PublishStatus";

import {
  compose,
  lengthValidator,
  imageTypesValidator,
  fileSizeValidator,
  startDateDayOfWeekValidator,
  imageIsCorruptedValidator,
} from "utils/validators";

import { ACCEPTED_IMAGE_TYPES } from "constants/image";
import {
  FOOD_PROGRAM_TYPES,
  PUBLISH_STATUSES,
  STRUCTURE_TYPES,
} from "constants/models";

import {
  Container,
  TitleContainer,
  ControlRow,
  Buttons,
  ButtonContainer,
} from "./styles";
import GreetingStore from "../../../../stores/GreetingStore";
import FoodActiveProgramDictionary from "../../../../stores/dictionary/FoodActiveProgramDictionary";

type FormSubmitData = {
  values: any;
};

type Props = {
  isLoading: boolean;
  onClose: () => void;
  onChangeIsDirty: (params: { dirty: boolean }) => void;
  onSubmit: (data: FormSubmitData) => void;
  initialValues?: any;
  isFormLoading?: boolean;
};

const MAX_FILE_SIZE_IN_MB = 10;

const FOOD_PROGRAM_TYPE_OPTIONS = [
  { label: "6-Week Program", value: FOOD_PROGRAM_TYPES.SIX_WEEK },
  { label: "VIP & Other", value: FOOD_PROGRAM_TYPES.VIP_AND_OTHER },
];
@observer
class GreetingForm extends React.Component<Props> {
  @observable updateConfirmationIsOpened: boolean = false;

  get isEditMode() {
    const { initialValues } = this.props;
    return !!initialValues;
  }

  get formTitle() {
    return !this.isEditMode ? "Add Greeting" : "Edit Greeting";
  }

  @computed get initialValues() {
    return this.isEditMode
      ? GreetingStore.details?.formData
      : { structureType: STRUCTURE_TYPES.STANDARD };
  }

  @computed get publishStatus() {
    const publishStatus = GreetingStore.details?.formData?.publishStatus;

    return publishStatus;
  }

  componentDidMount() {
    const { initialValues } = this.props;

    if (initialValues?.id) {
      GreetingStore.fetchDetails(initialValues.id);
    }
  }

  componentWillUnmount() {
    GreetingStore.resetDetails();
  }

  formFileValidator = (acceptedTypes: string) => (value?: File) =>
    imageTypesValidator(value?.type ?? "", acceptedTypes);

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
  };

  confirmUpdate = (values: any) => () => {
    const { onSubmit } = this.props;

    this.closeUpdateConfirmation();
    onSubmit(values);
  };

  onSubmit = (values: any) => {
    const { onSubmit } = this.props;

    const shouldShowUpdateConfirmation =
      this.publishStatus &&
      [
        PUBLISH_STATUSES.PUBLISHED,
        PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
      ].includes(this.publishStatus);

    shouldShowUpdateConfirmation
      ? this.openUpdateConfirmation()
      : onSubmit(values);
  };

  render() {
    const { onClose, isFormLoading, onChangeIsDirty } = this.props;

    return (
      <FormControl onSubmit={this.onSubmit} initialValues={this.initialValues}>
        {this.updateConfirmationIsOpened && (
          <FormSpy subscription={{ values: true }}>
            {({ values }) => (
              <Popup onClose={this.closeUpdateConfirmation} withoutExitButton>
                <ConfirmationDialog
                  title="Save Changes to Production"
                  text="All changes made will be published to production. Are you sure you want to proceed?"
                  acceptText="Proceed"
                  declineText="Cancel"
                  onAccept={this.confirmUpdate(values)}
                  onDecline={this.closeUpdateConfirmation}
                />
              </Popup>
            )}
          </FormSpy>
        )}
        <FormSpy subscription={{ dirty: true }} onChange={onChangeIsDirty} />
        <Container>
          <TitleContainer>
            <Typography variant="h1" color="textPrimary">
              {this.formTitle}
            </Typography>
            {this.isEditMode && this.publishStatus && (
              <PublishStatus status={this.publishStatus} />
            )}
          </TitleContainer>

          {!isFormLoading ? (
            <>
              <Row>
                <UploadPreviewFieldControl
                  name="file"
                  title="Image"
                  acceptedFiles={ACCEPTED_IMAGE_TYPES}
                  validate={compose(
                    this.formFileValidator(ACCEPTED_IMAGE_TYPES),
                    fileSizeValidator(MAX_FILE_SIZE_IN_MB),
                    imageIsCorruptedValidator,
                  )}
                  required
                />
              </Row>
            </>
          ) : (
            <Row>
              <Loader />
            </Row>
          )}
        </Container>

        <Buttons>
          <ButtonContainer>
            <Button
              variant="outlined"
              color="primary"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
          </ButtonContainer>
          <ButtonContainer>
            <SubmitButtonControl render={this.renderButton} />
          </ButtonContainer>
        </Buttons>
      </FormControl>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={this.props.isLoading}
      fullWidth
    >
      {!this.isEditMode ? "Add" : "Save"}
    </LoadingButton>
  );
}

export default GreetingForm;
